import { BAR_CHART_COLORS, COLUMN_CHART_COLORS, DONUT_CHART_COLORS, TREEMAP_CHART_COLORS, WHITE, AREA_CHART_COLORS, 
    AVERAGE_CHART_COLORS, TRAFFIC_LIGHT_COLORS, TRAFFIC_LIGHT_PLUS_COLORS } from './colors';
import Highcharts from 'highcharts';

//Setup Highcharts Global Options
Highcharts.setOptions({
    lang: {
        thousandsSep: ','
    }
});

export enum TooltipHeaderType {
    Resident = 1
}

export enum ChartType {
    Bar = 'bar',
    Column = 'column',
    Donut = 'donut',
    Area = 'area'
}

export enum TrafficLightColorChartType{
    TotalDailyDoseOfAcetaminophen = 'Total Daily Dose of Acetaminophen',
    MorphineEquivalentForOpioids = 'Morphine Equivalent for Opioids'
}

export function myHeaderFormatter(tooltipHeaderType: TooltipHeaderType){

        if(tooltipHeaderType === TooltipHeaderType.Resident) {
            return "<span style='font-size: 10px'>Residents: <b>{point.y}</b></span><br/>";
        }
    }



export function myPointFormatterResidents(point: any) {
        return `<span style='color:${point.color}'>\u25CF</span> ${point.series.name}</b>`;
    }

export function myPointFormatter(point: any, valueFormatter?: (value: number) => string) {
    // We use a single space to indicate an unnamed series. Setting it to undefined or empty string causes
    // Highcharts to call it 'Series 1'.
    if (point.series.name !== ' ') {
        return `<span style='color:${point.color}'>\u25CF</span> ${point.series.name}: <b>${valueFormatter(point.y)}</b>`;
    }
    else {
        return `<b>${valueFormatter(point.y)}</b>`;
    }
}
export function myLegendFormatter(point: any) {
    if (point.name.startsWith('hidden-field')) {
        point.color = WHITE;

        return '<span style="visibility: hidden">' + point.name + '</span>'
    }
    return point.name;
}

export function pastSixMonths() {
    var currentMonth: string[] = ["January", "February", "March", "April", "May", "June", "July", "August",
        "September", "October", "November", "December",];

    var month = new Date();
    var first = (month.getMonth() + 7) % 12;
    let list = [];
    while (first != month.getMonth()) {
        list.push(currentMonth[first]);
        first = (first + 1) % 11;
    }
    return list;
}


/**
 * Utility functions for creating Higcharts Options objects for different types of charts.
 */
export class ChartOptionsFactory {
    public static readonly subtitleStyleNormal = { "font-size": "120%", "color": "black" };
    public static readonly subtitleStyleNoData = { "font-size": "200%", "color": "#999999" };
    public static readonly subtitleStyleWithData = { "font-size": "250%", "color": "black" };

    private static readonly loadingOptions: Highcharts.LoadingOptions = {
        style: {
            background: "transparent url('assets/img/loading.gif') no-repeat center center",
            color: "transparent",
            opacity: 1
        },
        labelStyle: {
            color: "transparent"
        }
    };

    /**
     * When the title's text wraps HighCharts automatically adds a tooltip that we don't want.
     * This circumvents that by forcing it to render the title as html
     *@param ops
     *@param prop The property within the Highcharts.Options object. By default prop = 'title'
     *@returns {Highcharts.Options}
     */
    public static removeTitleTooltip(ops: Highcharts.Options, prop: string = 'title') {
        if (!(prop in ops)) {
            return;
        }
        ops[prop].useHTML = true;
        ops[prop].style = {
            "text-align": "center"
        };

        return ops;
    }

    /**
     * Method to add invisible chart elements that help align charts by ensuring all charts have an equal number of items in the legend
     * @param aggData
     * @param requiredNumberOfItems
     */
    public static addHiddenLegendItems(aggData: any, requiredNumberOfItems: number) {
        //In the off chance that the data from the server is null, just skip this
        if (!aggData || !aggData.ItemGroups) {
            return;
        }
        if (aggData.ItemGroups.length < requiredNumberOfItems) {
            let neededRows = 6 - aggData.ItemGroups.length;
            for (let i = 0; i < neededRows; i++) {
                aggData.ItemGroups.push({
                    ItemCount: -1,
                    GroupName: 'hidden-field-' + i
                });
            }
        }
    }

    /**
     * Method to disable hovering over invisible chart elements
     * @param chart
     */
    public static disableHiddenItemsHoverInLegend(chart: Highcharts.Chart) {
        if (chart.series.length) {
            chart.series[0].data.forEach(function (point: any) {
                if (point.name.startsWith('hidden-field-') && point.legendItem.element) {
                    point.legendItem.element.onmouseover = function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                    };
                }
            });
        }
    }

    /**
     * Build the basic Options for a grouped bar chart.
     * @param chartTitle
     * @param xAxisTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildGroupedBarType(chartTitle: string,
        xAxisTitle: string,
        valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = ChartOptionsFactory.buildStackedBarType(chartTitle, xAxisTitle, valueFormatter, null);
        ops.plotOptions.bar.stacking = undefined;
        ops.plotOptions.bar.grouping = true;
        return ops;
    }

    /**
     * Build the basic Options for a stacked bar chart.
     * @param chartTitle
     * @param xAxisTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildStackedBarType(chartTitle: string,
        xAxisTitle: string,
        valueFormatter?: (value: number) => string,
        tooltipFormatter?: (point: any) => string): Highcharts.Options {
        let ops: Highcharts.Options = {
            chart: {
                type: 'bar',
                marginRight: 30
            },
            credits: {
                enabled: false
            },
            title: {
                text: chartTitle
            },
            subtitle: {
                text: '',
                style: ChartOptionsFactory.subtitleStyleNoData,
                verticalAlign: 'middle',
                y: 0
            },
            plotOptions: {
                bar: {
                    stacking: 'normal'
                }
            },
            tooltip: {
            },
            legend: {},
            xAxis: [{
                categories: []
            }],
            yAxis: {
                allowDecimals: false,
                reversedStacks: false,
                min: 0,
                title: {
                    text: xAxisTitle
                },
                labels: {
                }
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: BAR_CHART_COLORS,
            series: []
        };

        if (valueFormatter) {
            ops.tooltip.pointFormatter = function () { return myPointFormatter(this, valueFormatter); };
            ops.yAxis['labels'].formatter = function () { return valueFormatter(this.value); };
        }
        if (tooltipFormatter) {
            ops.tooltip.pointFormatter = function () {
                return tooltipFormatter(this);
            };
        }
        // Needs special logic for when tooltip and value formatters are set

        return ops;
    }

    /**
     * Build the basic Options for a stacked bar chart where second color is aggregate of other data.
     * @param chartTitle
     * @param xAxisTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildStackedAggregateBarType(chartTitle: string,
        xAxisTitle: string,
        valueFormatter?: (value: number) => string,
        tooltipFormatter?: (point: any) => string): Highcharts.Options {
        let ops: Highcharts.Options = {
            chart: {
                type: 'bar',
                marginRight: 30
            },
            credits: {
                enabled: false
            },
            title: {
                text: chartTitle
            },
            subtitle: {
                text: '',
                style: ChartOptionsFactory.subtitleStyleNoData,
                verticalAlign: 'middle',
                y: 0
            },
            plotOptions: {
                bar: {
                    stacking: 'normal'
                }
            },
            tooltip: {
            },
            legend: {},
            xAxis: [{
                categories: []
            }],
            yAxis: {
                allowDecimals: false,
                reversedStacks: false,
                min: 0,
                title: {
                    text: xAxisTitle
                },
                labels: {
                }
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: AVERAGE_CHART_COLORS,
            series: []
        };

        if (valueFormatter) {
            ops.tooltip.pointFormatter = function () { return myPointFormatter(this, valueFormatter); };
            ops.yAxis['labels'].formatter = function () { return valueFormatter(this.value); };
        }
        if (tooltipFormatter) {
            ops.tooltip.pointFormatter = function () {
                return tooltipFormatter(this);
            }
        }
        // Needs special logic for when tooltip and value formatters are set

        return ops;
    }

    /**
     * Build the basic Options for a stacked bar chart.
     * @param chartTitle
     * @param xAxisTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildBarType(chartTitle: string,
        xAxisTitle: string,
        valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = ChartOptionsFactory.buildStackedBarType(chartTitle, xAxisTitle, valueFormatter, null);
        ops.plotOptions = {
            bar: {
                grouping: false
            }
        };
        if (valueFormatter) {
            ops.tooltip.formatter = function () { return valueFormatter(this.y) };
        } else {
            ops.tooltip.formatter = function () { return this.y.toString() }
        }

        return ops;
    }

    /**
     * Build the basic Options for a stacked bar chart.
     * @param chartTitle
     * @param xAxisTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildBarTypeWithDecimal(chartTitle: string,
        xAxisTitle: string,
        valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = this.buildBarType(chartTitle, xAxisTitle, valueFormatter);
        ops.yAxis['allowDecimals'] = true;

        return ops;
    }

    /**
     * Build the basic Options for a grouped column chart.
     * @param chartTitle
     * @param yAxisTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildColumnTypeWithDecimal(chartTitle: string,
        yAxisTitle: string,
        valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = ChartOptionsFactory.buildColumnType(chartTitle, yAxisTitle, valueFormatter);
        ops.yAxis['allowDecimals'] = true;

        return ops;
    }

    /**
     * Build the basic Options for a grouped column chart.
     * @param chartTitle
     * @param yAxisTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildGroupedColumnType(chartTitle: string,
        yAxisTitle: string,
        valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = ChartOptionsFactory.buildColumnType(chartTitle, yAxisTitle, valueFormatter);
        ops.plotOptions.column.stacking = undefined;
        ops.plotOptions.column.grouping = true;
        return ops;
    }

    /**
     * Build the basic Options for a stacked column chart.
     * @param chartTitle
     * @param yAxisTitle
     * @param tooltipHeaderType optional enumerator to select tooltip header format 
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildColumnType(chartTitle: string,
                                  yAxisTitle: string,                              
                                  valueFormatter?: (value: number) => string,
                                  tooltipHeaderType?: TooltipHeaderType,
                                 ): Highcharts.Options {
        let ops: Highcharts.Options = {
            chart: {
                type: 'column'
            },
            credits: {
                enabled: false
            },
            title: {
                text: chartTitle,
                align: "center"
            },
            subtitle: {
                text: '',
                style: ChartOptionsFactory.subtitleStyleNoData,
                verticalAlign: 'middle',
                y: 0
            },
            plotOptions: {
                column: {
                    stacking: 'normal'
                }
            },
            legend: {
            },
            tooltip: {
             },
            xAxis: {
                categories: []
            },
            yAxis: {
                allowDecimals: false,
                reversedStacks: false,
                min: 0,
                title: {
                    text: yAxisTitle
                },
                labels: {

                }
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: COLUMN_CHART_COLORS,
            series: []
        };

        if(!tooltipHeaderType){
            if (valueFormatter) {
                ops.tooltip.pointFormatter = function () { return myPointFormatter(this, valueFormatter); };
                ops.yAxis['labels'].formatter = function () { return valueFormatter(this.value); };
            }
        }else if(tooltipHeaderType === TooltipHeaderType.Resident){
                ops.tooltip.pointFormatter = function () { return myPointFormatterResidents(this); };
             }

        if(tooltipHeaderType){
            ops.tooltip.headerFormat = myHeaderFormatter(tooltipHeaderType);
        }


        return ops;
    }

    /**
     * Build the basic Options for a donut (pie with sub-title in middle) chart.
     * @param chartTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildDonutType(chartTitle: string,
                                 valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = {
            chart: {
                type: 'pie'
            },
            credits: {
                enabled: false
            },
            title: {
                text: chartTitle
            },
            subtitle: {
                text: '',
                style: ChartOptionsFactory.subtitleStyleNoData,
                verticalAlign: 'middle',
                align: 'center',
                y: 0
            },
            plotOptions: {
                pie: {
                    innerSize: '80%',
                    dataLabels: {
                        enabled: false
                    },
                    showInLegend: true,
                    point: {
                        events: {
                            legendItemClick: function (e) {
                                if (this.name.startsWith('hidden-field')) {
                                    e.preventDefault();
                                }
                            },
                            mouseOver: function (f) {
                                if (this.y < 0) {
                                    this.setState('hover');
                                }
                            }
                        }
                    }

                }
            },
            legend: {
                labelFormatter: function () {
                    return myLegendFormatter(this);
                },
                useHTML: true
            },
            tooltip: {
                headerFormat: "<span style='font-size: 10px'>{point.key}</span>: ",
                pointFormat: "<b>{point.y:,.0f}</b>"
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: DONUT_CHART_COLORS,
            series: []
        };

        if (valueFormatter) {
            ops.tooltip.pointFormatter = function () { return myPointFormatter(this, valueFormatter); };
        }

        return ops;
    }



   /**
    * Build the basic Options for a half-donut (semi-circle pie) chart.
    * @param chartTitle
    * @param chartColors
    * @param valueFormatter callback to format a point value
    * @returns {Highcharts.Options}
    */
    public static buildSemiCircleDonutType(chartTitle: string,
                                           chartColors: string[] = TRAFFIC_LIGHT_COLORS,
                                           valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = {
            chart: {
                type: 'pie'
            },
            credits: {
                enabled: false
            },
            title: {
                text: chartTitle
            },
            subtitle: {
                text: '',
                style: ChartOptionsFactory.subtitleStyleWithData,
                verticalAlign: 'middle',
                y: 270
            },
            plotOptions: {
                pie: {
                    center: ['50%', '85%'],
                    innerSize: '40%',
                    size: '140%',
                    dataLabels: {
                        enabled: false
                    },
                    startAngle: -90,
                    endAngle: 90,
                    showInLegend: true,
                    point: {
                        events: {
                            legendItemClick: function (e) {
                                if (this.name.startsWith('hidden-field')) {
                                    e.preventDefault();
                                }
                            },
                            mouseOver: function (f) {
                                if (this.y < 0) {
                                    this.setState('hover');
                                }
                            }
                        }
                    }
                }
            },
            legend: {
                labelFormatter: function () {
                    return myLegendFormatter(this);
                },
                symbolWidth: 5,
                itemDistance: 15,
                useHTML: true
            },
            tooltip: {
                headerFormat: "<span style='font-size: 10px'>{point.key}</span>: ",
                pointFormat: "<b>{point.y:,.0f}</b>",
                useHTML: true
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: chartColors,
            series: []
        };

        if (valueFormatter) {
            ops.tooltip.pointFormatter = function () { return myPointFormatter(this, valueFormatter); };
        }

        return ops;
    }


    /**
     * Build the basic Options for a pie chart.
     * @param chartTitle
     * @param valueFormatter callback to format a point value
     * @returns {Highcharts.Options}
     */
    public static buildPieType(chartTitle: string,
                               valueFormatter?: (value: number) => string): Highcharts.Options {
        let ops: Highcharts.Options = {
            chart: {
                type: 'pie'
            },
            credits: {
                enabled: false
            },
            title: {
                text: chartTitle
            },
            subtitle: {
                text: '',
            },
            plotOptions: {
                pie: {
                    dataLabels: {
                        enabled: false
                    },
                    showInLegend: true,
                }
            },
            tooltip: {
                headerFormat: "<span style='font-size: 10px'>{point.key}</span>: ",
                pointFormat: "<b>{point.y}</b>",
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: DONUT_CHART_COLORS,
            series: []
        };

        if (valueFormatter) {
            ops.tooltip.pointFormatter = function () { return myPointFormatter(this, valueFormatter); };
        }

        return ops;
    }

    /**
     * Build the basic Options for a treemap chart.
     * @param chartTitle
     * @returns {Highcharts.Options}
     */
    public static buildTreemapType(chartTitle: string): Highcharts.Options {
        let ops: Highcharts.Options = {
            chart: {
            },
            credits: {
                enabled: false
            },
            title: {
                text: chartTitle
            },
            subtitle: {
                text: undefined
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: TREEMAP_CHART_COLORS,
            series: [{
                type: "treemap",
                layoutAlgorithm: 'stripes',
                alternateStartingDirection: true,
                levels: [{
                    level: 1,
                    layoutAlgorithm: 'sliceAndDice',
                    dataLabels: {
                        enabled: true,
                        align: 'left',
                        verticalAlign: 'top',
                        style: {
                            fontSize: '18px',
                            fontWeight: 'bold',
                        }
                    }
                }, {
                    level: 2,
                    dataLabels: {
                        enabled: true,
                        style: {
                            fontSize: "14px",
                        }
                    }
                }],
                data: []
            }]
        };

        return ops;
    }

    public static buildAreaType(chartTitle: string, yAxisText: string) {

        let ops: Highcharts.Options = {
            chart: {
                type: 'area'
            },
            title: {
                text: chartTitle
            },
            yAxis: {
                title: {

                    text: yAxisText,
                   // min: 0
                }
            },

            tooltip: {
                headerFormat: "<span style='font-size: 10px'>{point.key}</span>: ",
                pointFormat: "<b>{point.y}</b>",
            },
            plotOptions: {
                area: {
                    //stacking: 'normal',
                    lineWidth: 0,
                    lineColor: AREA_CHART_COLORS[0], //TODO Requires Testing
                    marker: {
                        enabled: false,
                        symbol: 'circle',
                        radius: 2,
                        states: {
                            hover: {
                                enabled: true
                            }
                        }
                    }
                }
            },
            legend: {
                //  enabled: false
            },
            credits: {
                enabled: false
            },
            loading: ChartOptionsFactory.loadingOptions,
            colors: AREA_CHART_COLORS
        };

        return ops;
    }

    /**
     * Build the basic Options for a stacked area chart.
     * @param chartTitle
     * @param yAxisText
     * @returns {Highcharts.Options}
     */
    public static buildStackedAreaType(chartTitle: string, yAxisText: string) {

        let ops: Highcharts.Options = this.buildAreaType(chartTitle, yAxisText);
        ops.plotOptions.area.stacking = 'normal';
        ops.legend.enabled = true;

        ops.tooltip = {
            headerFormat: "<span style='font-size: 10px'>{point.series.name}</span>: ",
            pointFormat: "<b>{point.y}</b>",
        };

        return ops;
    }
}

